import { fmt } from '../../utils';

/**
 * Оформление подписи оси диаграммы.
 *
 * Цвет задан переменной темы, а не классом-утилитой: подписи осей recharts рисует атрибутом
 * `fill` на своём `<text>`, и класс потребителя до него не доходит.
 */
export const CHART_AXIS_TICK = { fill: 'var(--color-muted-foreground)', fontSize: 12 };

/** Цвет линий осей и сетки — тот же токен границы, что и у остальных блоков дашборда. */
export const CHART_LINE_COLOR = 'var(--color-border)';

/** Цвет линии и столбцов диаграммы. */
export const CHART_SERIES_COLOR = 'var(--color-primary)';

/** Цвет незаполненной дорожки полосы — тот же, что у дорожек показателей стандартного дашборда. */
export const CHART_TRACK_COLOR = 'var(--color-muted)';

/** Наибольшее место под название элемента — то же, что у полос показателей стандартного дашборда. */
export const CHART_CATEGORY_MAX_WIDTH = 180;

/**
 * Промежуток между концом названия и началом полосы: названия стоят у полос, не вплотную к ним, а
 * на правящемся дашборде посередине промежутка стоит граница колонки названий.
 */
export const CHART_CATEGORY_LABEL_GAP = 8;

/** Наименьшее место под название — то же, что у полос стандартного дашборда. */
const CHART_CATEGORY_MIN_WIDTH = 60;

/** Наименьшая длина дорожки, которую оставляет заданная пользователем ширина названий. */
const CHART_TRACK_MIN_WIDTH = 48;

/** Наибольшая доля свободной ширины под названия: остальное остаётся дорожке. */
const CHART_CATEGORY_MAX_SHARE = 0.35;

/**
 * Место под названия элементов по свободной ширине диаграммы — той, что осталась после величин.
 *
 * Постоянной шириной место не отмеряется: в тесной доле сетки название заняло бы её большую часть,
 * и дорожке — тому, ради чего диаграмма и нужна, — длины не осталось бы. Уступает место название, а
 * не дорожка и не величина: усечённое название остаётся доступным подсказкой, а укоротить дорожку
 * или перенести величину на вторую строку нечем.
 */
export const chartCategoryWidth = (freeWidth: number): number =>
  Math.round(
    Math.min(CHART_CATEGORY_MAX_WIDTH, Math.max(CHART_CATEGORY_MIN_WIDTH, freeWidth * CHART_CATEGORY_MAX_SHARE)),
  );

/**
 * Место под названия в пределах, заданных пользователю: полосе остаётся длина, а величине у полосы —
 * своё место целиком; уже наименьшего место не становится — в нём видно начало названия.
 */
export const clampChartLabelColumnWidth = (widthPx: number, chartWidth: number, valueWidth: number): number =>
  Math.round(Math.max(CHART_CATEGORY_MIN_WIDTH, Math.min(widthPx, chartWidth - valueWidth - CHART_TRACK_MIN_WIDTH)));

/**
 * Место под названия элементов полос: заданное пользователем долей ширины диаграммы — в своих
 * пределах, без него — отмеренное по свободной ширине ({@link chartCategoryWidth}).
 *
 * @param labelWidth - Доля ширины диаграммы под названия; не задана — место отмеряется само
 */
export const chartLabelColumnWidth = (
  chartWidth: number,
  valueWidth: number,
  labelWidth: number | null | undefined,
): number =>
  labelWidth === null || labelWidth === undefined
    ? chartCategoryWidth(Math.max(0, chartWidth - valueWidth))
    : clampChartLabelColumnWidth(labelWidth * chartWidth, chartWidth, valueWidth);

/**
 * Подпись величины на оси.
 *
 * Обёртка вокруг {@link fmt} нужна из-за подписи вызова: recharts передаёт форматтеру вторым
 * доводом номер деления, и он занял бы место точности.
 */
export const formatAxisValue = (value: number): string => fmt(value);
